/**
 * ai-viet-prompt-dialog.tsx — "AI viết": tạo mới hoặc tối ưu tài liệu train
 * đang mở ở màn Train. Người dùng mô tả thêm nếu muốn; để trống thì AI tự
 * viết đúng vai của loại tài liệu theo ngữ cảnh tổ chức/bot. Kết quả đổ vào
 * ô soạn thảo, chưa lưu — người dùng xem rồi bấm "Lưu tài liệu".
 */
import { useState } from 'react'
import { Sparkles, Wand2, RefreshCw, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { soanPromptBangAi, type LoaiPrompt } from '@/hooks/use-ai'

export const NHAN_LOAI_PROMPT: Record<LoaiPrompt, string> = {
  persona: 'Tính cách & xưng hô',
  playbook: 'Playbook',
  handoff_rules: 'Luật chuyển nhân viên',
  index: 'Vai trò & nhiệm vụ',
  mechanism: 'Cách AI vận hành',
  criteria: 'Tiêu chí bắt buộc (guardrails)',
  scenario: 'Kịch bản',
}

const GOI_Y: Record<LoaiPrompt, string> = {
  persona: 'vd: xưng "em", gọi "anh/chị", giọng ấm nhưng ngắn, không emoji',
  playbook: 'vd: tập trung tư vấn quà biếu doanh nghiệp, hỏi ngân sách trước',
  handoff_rules: 'vd: chuyển ngay khi khách nhắc hoàn tiền hoặc đơn > 5 triệu',
  index: 'vd: bot phục vụ nick Zalo cá nhân của nhân viên, mục tiêu chốt đơn',
  mechanism: 'vd: nhấn mạnh giá chỉ lấy từ catalog, chế độ gợi ý cho nhân viên duyệt',
  criteria: 'vd: cấm emoji, mỗi tin tối đa 3 câu, luôn xin SĐT trước khi báo phí ship',
  scenario: 'vd: khách hỏi mua trà cho người tiểu đường',
}

export function AiVietPromptDialog({ loai, noiDungHienTai, botId, kichBan, onClose, onKetQua }: {
  loai: LoaiPrompt
  noiDungHienTai: string
  botId?: string
  kichBan?: { name?: string; description?: string; triggerHints?: string }
  onClose: () => void
  onKetQua: (kq: { content: string; kichBan?: { name: string; description: string; triggerHints: string } }) => void
}) {
  const coNoiDung = noiDungHienTai.trim().length > 0
  const [cheDo, setCheDo] = useState<'tao_moi' | 'toi_uu'>(coNoiDung ? 'toi_uu' : 'tao_moi')
  const [moTa, setMoTa] = useState('')
  const [dangChay, setDangChay] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)

  async function chay(tuDong: boolean) {
    setDangChay(true)
    setLoi(null)
    try {
      const kq = await soanPromptBangAi({
        loai, cheDo,
        moTa: tuDong ? undefined : moTa.trim() || undefined,
        noiDungHienTai: cheDo === 'toi_uu' ? noiDungHienTai : undefined,
        botId, kichBan,
      })
      onKetQua(kq)
      onClose()
    } catch (err) {
      setLoi(apiError(err))
    } finally {
      setDangChay(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !dangChay && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> AI viết · {NHAN_LOAI_PROMPT[loai]}
          </DialogTitle>
          <DialogDescription>
            Dùng model GPT của tổ chức. AI đọc ngữ cảnh thật (công ty, vai trò bot, danh mục, các tài liệu khác) rồi viết đúng vai của loại tài liệu này. Kết quả đổ vào ô soạn thảo — anh/chị xem rồi mới bấm Lưu.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 rounded-md border p-0.5">
          {([['tao_moi', 'Tạo mới prompt'], ['toi_uu', 'Tối ưu prompt']] as const).map(([k, nhan]) => (
            <button
              key={k} type="button" disabled={dangChay || (k === 'toi_uu' && !coNoiDung)}
              onClick={() => setCheDo(k)}
              title={k === 'toi_uu' && !coNoiDung ? 'Chưa có nội dung để tối ưu' : undefined}
              className={`flex-1 rounded px-2 py-1.5 text-sm disabled:opacity-40 ${cheDo === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            >
              {nhan}
            </button>
          ))}
        </div>

        <div>
          <Label className="text-xs">
            {cheDo === 'toi_uu' ? 'Muốn tối ưu theo hướng nào?' : 'Mô tả yêu cầu'}{' '}
            <span className="font-normal text-muted-foreground">(không bắt buộc)</span>
          </Label>
          <textarea
            className="mt-1 min-h-[96px] w-full rounded-md border bg-transparent p-2 text-sm outline-none focus:ring-1 focus:ring-ring"
            value={moTa} onChange={(e) => setMoTa(e.target.value)} disabled={dangChay}
            placeholder={GOI_Y[loai]}
          />
        </div>

        {cheDo === 'toi_uu' && (
          <p className="text-[11px] text-muted-foreground">
            Tối ưu giữ ý đúng của bản hiện tại ({noiDungHienTai.length.toLocaleString('vi-VN')} ký tự), sửa chỗ mơ hồ/thừa/mâu thuẫn và bổ sung chỗ thiếu.
          </p>
        )}
        {loi && <p className="text-sm text-destructive">{loi}</p>}

        <div className="flex flex-col gap-2">
          <Button onClick={() => void chay(false)} disabled={dangChay}>
            {dangChay ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {dangChay ? 'AI đang viết…' : cheDo === 'toi_uu' ? 'Tối ưu hoá' : 'Viết prompt'}
          </Button>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="h-px flex-1 bg-border" />hoặc<span className="h-px flex-1 bg-border" />
          </div>
          <Button variant="outline" onClick={() => void chay(true)} disabled={dangChay} title="Bỏ qua mô tả — AI tự viết theo ngữ cảnh">
            <RefreshCw className="h-4 w-4" /> Tự động {cheDo === 'toi_uu' ? 'tối ưu' : 'viết'} theo ngữ cảnh
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
